import React from "react";

import { Grid, Link } from "@mui/material";
import classNames from "classnames";

import { ValueType } from "../../types";
import PhoneFieldWithValidation from "../inputs/PhoneFieldWithValidation";
import TextFieldWithValidation from "../inputs/TextFieldWithValidation";

interface ComponentPropsType {
  canEditUserProfileInfo?: boolean;
  classes: Record<string, string>;
  changePhoneCountry: (country: string) => void;
  disableEmailField?: boolean;
  disablePhoneField?: boolean;
  email: ValueType;
  firstName: ValueType;
  handleFieldChange: (field: string, value: string) => void;
  lastName: ValueType;
  phoneCountry: { value: string | undefined };
  phoneNumber: ValueType;
  validateEmail: (email: string) => void;
  validatePhoneNumber: (phone: string, country: string | undefined) => void;
  validateText: (event: React.FocusEvent<HTMLInputElement>) => void;
}

const BasicInfoComponent = (props: ComponentPropsType) => {
  const {
    canEditUserProfileInfo = true,
    classes,
    changePhoneCountry,
    disableEmailField = false,
    disablePhoneField = false,
    email,
    firstName,
    handleFieldChange,
    lastName,
    phoneCountry,
    phoneNumber,
    validateEmail,
    validatePhoneNumber,
    validateText,
  } = props;
  return (
    <>
      <Grid container item spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextFieldWithValidation
            autoComplete="given-name"
            id="first-name"
            inputId="first-name"
            label="First Name"
            margin="normal"
            variant="outlined"
            name="firstName"
            value={firstName.value}
            fullWidth
            onBlur={validateText}
            error={firstName.error}
            className={classNames(classes.fullWidth, {
              [classes.disabledInputField]: !canEditUserProfileInfo,
            })}
            required
            userForm={true}
            onChange={(event) =>
              handleFieldChange("firstName", event.target.value)
            }
            disabled={!canEditUserProfileInfo}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextFieldWithValidation
            autoComplete="family-name"
            id="last-name"
            inputId="last-name"
            label="Last Name"
            margin="normal"
            variant="outlined"
            name="lastName"
            value={lastName.value}
            fullWidth
            onBlur={validateText}
            error={lastName.error}
            className={classNames(classes.fullWidth, {
              [classes.disabledInputField]: !canEditUserProfileInfo,
            })}
            userForm={true}
            onChange={(event) =>
              handleFieldChange("lastName", event.target.value)
            }
            disabled={!canEditUserProfileInfo}
          />
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <TextFieldWithValidation
          autoComplete="email"
          id="email-address"
          inputId="email-address"
          label="Email"
          margin="normal"
          variant="outlined"
          name="email"
          value={email.value}
          className={classNames(classes.textFieldFullWidth, {
            [classes.disabledInputField]:
              !canEditUserProfileInfo || disableEmailField,
          })}
          onBlur={(event) => validateEmail(event.target.value.trim())}
          error={email.error}
          helperText={
            email.error
              ? email.helperText === "error"
                ? [
                    "Error: Email validation failed. ",
                    <Link
                      key="link"
                      component="button"
                      color="inherit"
                      onClick={(event) => {
                        event.preventDefault();
                        validateEmail(
                          (
                            document.getElementsByName(
                              "email"
                            )[0] as HTMLInputElement
                          ).value.trim()
                        );
                      }}
                      className={classes.helperLink}
                    >
                      Click here to try again
                    </Link>,
                    ".",
                  ]
                : email.helperText
              : ""
          }
          fullWidth
          required
          userForm={true}
          onChange={(event) => handleFieldChange("email", event.target.value)}
          disabled={!canEditUserProfileInfo || disableEmailField}
        />
      </Grid>

      <Grid item xs={12}>
        <PhoneFieldWithValidation
          autoComplete="tel"
          id="phone-number"
          inputId="phone-number"
          label="Phone Number"
          margin="normal"
          variant="outlined"
          name="phoneNumber"
          className={classNames(classes.textFieldFullWidth, {
            [classes.disabledInputField]:
              !canEditUserProfileInfo || disablePhoneField,
          })}
          changePhoneCountry={changePhoneCountry}
          onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
            validatePhoneNumber(event.target.value.trim(), phoneCountry.value);
          }}
          value={phoneNumber.value}
          country={phoneCountry.value}
          error={phoneNumber.error}
          helperText={
            phoneNumber.error
              ? phoneNumber.helperText === "error"
                ? [
                    "Error: Phone number validation failed. ",
                    <Link
                      key="link"
                      component="button"
                      color="inherit"
                      onClick={(event) => {
                        event.preventDefault();
                        validatePhoneNumber(
                          (
                            document.getElementsByName(
                              "phoneNumber"
                            )[0] as HTMLInputElement
                          ).value.trim(),
                          phoneCountry.value
                        );
                      }}
                      className={classes.helperLink}
                    >
                      Click here to try again
                    </Link>,
                    ".",
                  ]
                : phoneNumber.helperText
              : ""
          }
          fullWidth
          userForm={true}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
            handleFieldChange("phoneNumber", event.target.value)
          }
          disabled={!canEditUserProfileInfo || disablePhoneField}
        />
      </Grid>
    </>
  );
};

export default BasicInfoComponent;
